import React from "react";
import { Controller } from "react-hook-form";
import { Switch } from "@mui/material";
import styles from "./ToggleInput.module.scss";
import { combineClasses } from "@/lib/utils";
import withLabelAndError from "../withLabelAndError";
import { WithLabelAndErrorComponent } from "@/types/formControls";

interface ToggleInputProps {
  // RHF (optional)
  control?: any;
  name?: string;

  // Normal usage
  value?: boolean;
  onChange?: (value: boolean) => void;

  // Common
  label?: string;
  disabled?: boolean;
  className?: string;
  noBorder?: boolean;
  bgColor?: boolean;
  labelVariant?: "normal" | "largeBold";
  subLabel?: string;
  labelClass?: string;
}

// 🎯 Pure UI
const BaseToggle: React.FC<any> = ({
  value,
  onChange,
  disabled,
  className,
  noBorder = false,
  bgColor = false,
  labelVariant = "normal",
  label,
  error,
  subLabel,
  labelClass,
}) => {
  return (
    <div
      className={combineClasses(
        styles.toggleContainer,
        error && styles.errorState,
        noBorder && styles.noBorder,
        bgColor && styles.bgColor,
        className,
      )}
      onClick={() => !disabled && onChange(!value)}
    >
      {label && (
        <span
          className={combineClasses(
            styles.toggleLabel,
            labelVariant === "largeBold" && styles.largeBoldLabel,
            labelClass
          )}
        >
          {label}
          {subLabel && <div className={styles.subLabel}>{subLabel}</div>}
        </span>
      )}

      <div onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          size="small"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

// 🎯 Main Component (supports both modes)
const ToggleInput: React.FC<ToggleInputProps> = ({
  control,
  name,
  value,
  onChange,
  ...rest
}) => {
  // RHF mode
  if (control && name) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <BaseToggle
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error}
            {...rest}
          />
        )}
      />
    );
  }

  // Normal mode
  return (
    <BaseToggle value={!!value} onChange={onChange || (() => {})} {...rest} />
  );
};

// ✅ Wrap with HOC
const ToggleWrapped = withLabelAndError(
  ToggleInput as WithLabelAndErrorComponent,
);

const ToggleWithLabelAndError: React.FC<any> = (props) => {
  return <ToggleWrapped {...props} hideOuterLabel={true} />;
};

export default ToggleWithLabelAndError;
export { ToggleInput };
